/**
 * LiveInterview page — real-time voice interview simulator.
 *
 * Architecture:
 *   1. POST /api/realtime/session   → get ephemeral client_secret + session_id
 *   2. Establish WebRTC:
 *      - createOffer / addTrack (mic) / ontrack (assistant audio)
 *      - createDataChannel("oai-events") for JSON events (transcripts, VAD, etc.)
 *   3. POST offer.sdp → /api/realtime/negotiate (backend proxies to OpenAI)
 *   4. setRemoteDescription(answer) → connection live, first assistant turn plays.
 *   5. Every event on the data channel updates the live transcript / speaking indicator.
 *   6. On end: POST /api/realtime/session/{id}/end with the full transcript → scorecard.
 *
 * Features implemented per spec:
 *   ✔ Big mic + waveform (via getUserMedia + AudioContext analyser)
 *   ✔ Live transcript (user + assistant)
 *   ✔ AI speaking indicator
 *   ✔ Session timer
 *   ✔ Current question
 *   ✔ End / Pause / Mute buttons
 *   ✔ Connection status
 *   ✔ Fullscreen mode
 *   ✔ Barge-in enabled server-side (turn_detection.interrupt_response = true)
 *   ✔ Continuous listening with server-side VAD
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Mic, MicOff, PhoneOff, Pause, Play, Maximize2, Minimize2,
  Radio, User as UserIcon, Bot, Sparkles, Crown,
} from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { useEntitlements } from "@/context/EntitlementsContext";
import { Link } from "react-router-dom";

const STATUS = {
  idle: "Ready to start",
  connecting: "Connecting…",
  connected: "Live",
  listening: "Listening",
  speaking: "Interviewer speaking",
  paused: "Paused",
  ended: "Ended",
  error: "Connection error",
};

export default function LiveInterview() {
  const { tier, isPaid } = useEntitlements();

  // Session config (pre-flight)
  const [config, setConfig] = useState({ personas: [], types: [], difficulties: [] });
  const [usage, setUsage] = useState(null);
  const [selection, setSelection] = useState({
    persona: "professional",
    interview_type: "behavioral",
    difficulty: "mid",
    role: "",
    company: "",
  });

  // Live session state
  const [status, setStatus] = useState("idle");
  const [sessionId, setSessionId] = useState(null);
  const [transcript, setTranscript] = useState([]);   // [{speaker, text, at}]
  const [currentQuestion, setCurrentQuestion] = useState("");
  const [isMuted, setIsMuted] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [elapsed, setElapsed] = useState(0);          // seconds
  const [isSpeaking, setIsSpeaking] = useState(false); // assistant speaking now?
  const [waveLevel, setWaveLevel] = useState(0);      // 0..1 mic input level
  const [fullscreen, setFullscreen] = useState(false);
  const [scorecard, setScorecard] = useState(null);   // populated after end

  // Refs for WebRTC lifecycle
  const pcRef = useRef(null);
  const dcRef = useRef(null);
  const localStreamRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const timerRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);
  const startedAtRef = useRef(0);
  const partialsRef = useRef({});   // active transcript deltas per item_id

  // ---------- Pre-flight: load config + usage ----------
  useEffect(() => {
    // eslint-disable-next-line no-console
    api.get("/realtime/config").then(r => setConfig(r.data)).catch(e => console.debug("[LiveInterview] config load:", e));
    // eslint-disable-next-line no-console
    api.get("/realtime/usage").then(r => setUsage(r.data)).catch(e => console.debug("[LiveInterview] usage load:", e));
  }, []);

  // ---------- Waveform driver ----------
  const startWaveform = useCallback((stream) => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      src.connect(analyser);
      analyserRef.current = analyser;
      const buf = new Uint8Array(analyser.frequencyBinCount);
      const tick = () => {
        analyser.getByteTimeDomainData(buf);
        let sumSq = 0;
        for (let i = 0; i < buf.length; i++) {
          const v = (buf[i] - 128) / 128;
          sumSq += v * v;
        }
        setWaveLevel(Math.min(1, Math.sqrt(sumSq / buf.length) * 3));
        rafRef.current = requestAnimationFrame(tick);
      };
      tick();
    } catch (e) {
      // AudioContext unavailable (rare — old browsers). Waveform will simply
      // stay flat. Not user-facing so we log to console for observability.
      // eslint-disable-next-line no-console
      console.debug("[LiveInterview] Waveform init failed:", e);
    }
  }, []);

  // ---------- Data-channel event handling ----------
  const handleEvent = useCallback((raw) => {
    let ev;
    try { ev = JSON.parse(raw); } catch (parseErr) {
      // eslint-disable-next-line no-console
      console.debug("[LiveInterview] Ignoring non-JSON event:", parseErr);
      return;
    }
    // console.debug("[oai-event]", ev.type, ev);
    const now = new Date().toISOString();
    switch (ev.type) {
      case "session.created":
      case "session.updated":
        setStatus("connected");
        break;
      case "input_audio_buffer.speech_started":
        // User started speaking — barge-in triggers server-side; UI reflect.
        setIsSpeaking(false);
        setStatus("listening");
        break;
      case "input_audio_buffer.speech_stopped":
        setStatus("connected");
        break;
      case "conversation.item.input_audio_transcription.delta": {
        const id = ev.item_id;
        partialsRef.current[id] = (partialsRef.current[id] || "") + (ev.delta || "");
        setTranscript((t) => {
          const existing = t.find((r) => r.item_id === id && r.speaker === "user");
          const text = partialsRef.current[id];
          if (existing) return t.map((r) => r === existing ? { ...r, text } : r);
          return [...t, { speaker: "user", text, item_id: id, at: now, partial: true }];
        });
        break;
      }
      case "conversation.item.input_audio_transcription.completed": {
        const id = ev.item_id;
        const final = ev.transcript || partialsRef.current[id] || "";
        delete partialsRef.current[id];
        setTranscript((t) => t.map((r) =>
          r.item_id === id && r.speaker === "user" ? { ...r, text: final, partial: false } : r
        ));
        break;
      }
      case "response.audio_transcript.delta": {
        const id = ev.response_id;
        partialsRef.current[id] = (partialsRef.current[id] || "") + (ev.delta || "");
        const text = partialsRef.current[id];
        setTranscript((t) => {
          const existing = t.find((r) => r.item_id === id && r.speaker === "assistant");
          if (existing) return t.map((r) => r === existing ? { ...r, text } : r);
          return [...t, { speaker: "assistant", text, item_id: id, at: now, partial: true }];
        });
        // Detect a question mark for the "current question" callout
        if (text && text.trim().endsWith("?")) setCurrentQuestion(text.trim().slice(-260));
        break;
      }
      case "response.audio_transcript.done": {
        const id = ev.response_id;
        const final = ev.transcript || partialsRef.current[id] || "";
        delete partialsRef.current[id];
        setTranscript((t) => t.map((r) =>
          r.item_id === id && r.speaker === "assistant" ? { ...r, text: final, partial: false } : r
        ));
        if (final && final.trim().endsWith("?")) setCurrentQuestion(final.trim().slice(-260));
        break;
      }
      case "output_audio_buffer.started":
      case "response.output_audio.delta":
      case "response.output_audio.start":
        setIsSpeaking(true);
        setStatus("speaking");
        break;
      case "output_audio_buffer.stopped":
      case "response.output_audio.done":
      case "response.done":
        setIsSpeaking(false);
        setStatus("connected");
        break;
      case "error":
        console.error("Realtime error", ev);
        toast.error(ev.error?.message || "Interview error");
        break;
      default:
        break;
    }
  }, []);

  // ---------- Start ----------
  const start = async () => {
    setStatus("connecting");
    setTranscript([]);
    setScorecard(null);
    partialsRef.current = {};
    try {
      // 1. Mint ephemeral token + create DB session
      const { data: s } = await api.post("/realtime/session", selection);
      setSessionId(s.session_id);

      // 2. Set up peer connection
      const pc = new RTCPeerConnection();
      pcRef.current = pc;

      // Remote audio
      const audioEl = remoteAudioRef.current || (() => {
        const el = document.createElement("audio");
        el.autoplay = true;
        document.body.appendChild(el);
        remoteAudioRef.current = el;
        return el;
      })();
      pc.ontrack = (e) => { audioEl.srcObject = e.streams[0]; };

      // 3. Add mic
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      localStreamRef.current = stream;
      stream.getTracks().forEach((t) => pc.addTrack(t, stream));
      startWaveform(stream);

      // 4. Data channel for events
      const dc = pc.createDataChannel("oai-events");
      dcRef.current = dc;
      dc.onmessage = (e) => handleEvent(e.data);
      dc.onopen = () => { setStatus("connected"); };
      dc.onerror = () => setStatus("error");

      // 5. Create offer + negotiate directly with OpenAI using ephemeral token
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      const negotiateResp = await fetch(
        `https://api.openai.com/v1/realtime?model=${encodeURIComponent(s.model)}`,
        {
          method: "POST",
          body: offer.sdp,
          headers: {
            Authorization: `Bearer ${s.client_secret}`,
            "Content-Type": "application/sdp",
          },
        },
      );
      if (!negotiateResp.ok) {
        const errText = await negotiateResp.text();
        throw new Error(`OpenAI negotiate failed (${negotiateResp.status}): ${errText.slice(0, 200)}`);
      }
      const answerSdp = await negotiateResp.text();
      await pc.setRemoteDescription({ type: "answer", sdp: answerSdp });

      // 6. Timer
      startedAtRef.current = Date.now();
      timerRef.current = setInterval(() => {
        setElapsed(Math.floor((Date.now() - startedAtRef.current) / 1000));
      }, 500);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || e.message || "Couldn't start interview");
      setStatus("error");
      await cleanup();
    }
  };

  // ---------- Controls ----------
  const toggleMute = () => {
    const s = localStreamRef.current;
    if (!s) return;
    const enabled = !isMuted; // if currently muted, we want to unmute
    s.getAudioTracks().forEach((t) => { t.enabled = enabled; });
    setIsMuted(!enabled);
  };

  const togglePause = () => {
    // "Pause" = mute mic + tell model to hold via response.cancel
    const dc = dcRef.current;
    if (!dc || dc.readyState !== "open") return;
    if (!isPaused) {
      dc.send(JSON.stringify({ type: "response.cancel" }));
      const s = localStreamRef.current;
      s?.getAudioTracks().forEach((t) => { t.enabled = false; });
      setStatus("paused");
    } else {
      const s = localStreamRef.current;
      s?.getAudioTracks().forEach((t) => { t.enabled = !isMuted; });
      setStatus("connected");
    }
    setIsPaused(!isPaused);
  };

  const cleanup = useCallback(async () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    // Best-effort teardown — these calls can throw if already closed, which
    // is expected during unmount. Log at debug level for observability.
    if (analyserRef.current) {
      try { analyserRef.current.disconnect(); }
      catch (e) { console.debug("[LiveInterview] analyser.disconnect:", e); }  // eslint-disable-line no-console
      analyserRef.current = null;
    }
    if (dcRef.current) {
      try { dcRef.current.close(); }
      catch (e) { console.debug("[LiveInterview] dc.close:", e); }  // eslint-disable-line no-console
      dcRef.current = null;
    }
    if (pcRef.current) {
      try { pcRef.current.close(); }
      catch (e) { console.debug("[LiveInterview] pc.close:", e); }  // eslint-disable-line no-console
      pcRef.current = null;
    }
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((t) => t.stop());
      localStreamRef.current = null;
    }
    setWaveLevel(0);
  }, []);

  const endInterview = async () => {
    const dur = Math.floor((Date.now() - startedAtRef.current) / 1000);
    setStatus("ended");
    await cleanup();
    if (sessionId) {
      try {
        // Send final transcript to backend
        const flat = transcript
          .filter((t) => t.text)
          .map((t) => ({ speaker: t.speaker, text: t.text, at: t.at }));
        await api.post(`/realtime/session/${sessionId}/end`, {
          duration_seconds: dur,
          transcript: flat,
          ended_by: "user",
        });
        toast.success("Session saved. Generating scorecard…");
        // Poll for scorecard
        setTimeout(() => pollScorecard(sessionId), 4000);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[LiveInterview] session end failed:", err);
        toast.error("Couldn't save session");
      }
    }
  };

  const pollScorecard = async (id, attempts = 0) => {
    if (attempts > 15) return;
    try {
      const { data } = await api.get(`/realtime/sessions/${id}`);
      if (data?.scorecard && data.scorecard.overall_score !== undefined) {
        setScorecard(data.scorecard);
        return;
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.debug("[LiveInterview] scorecard poll:", err);
    }
    setTimeout(() => pollScorecard(id, attempts + 1), 3000);
  };

  // ---------- Fullscreen ----------
  const toggleFullscreen = async () => {
    if (!document.fullscreenElement) {
      try { await document.documentElement.requestFullscreen?.(); }
      // eslint-disable-next-line no-console
      catch (e) { console.debug("[LiveInterview] requestFullscreen:", e); }
      setFullscreen(true);
    } else {
      try { await document.exitFullscreen?.(); }
      // eslint-disable-next-line no-console
      catch (e) { console.debug("[LiveInterview] exitFullscreen:", e); }
      setFullscreen(false);
    }
  };

  // ---------- Cleanup on unmount ----------
  useEffect(() => () => { cleanup(); }, [cleanup]);

  // ---------- Helpers ----------
  const fmtTime = (s) => {
    const mm = String(Math.floor(s / 60)).padStart(2, "0");
    const ss = String(s % 60).padStart(2, "0");
    return `${mm}:${ss}`;
  };
  const usageBanner = useMemo(() => {
    if (!usage) return null;
    if (usage.limit_minutes === null) return null;
    const remaining = Math.max(0, usage.limit_minutes - usage.used_seconds / 60);
    const pct = usage.limit_minutes > 0 ? (usage.used_seconds / 60) / usage.limit_minutes : 0;
    return { remaining, pct, ...usage };
  }, [usage]);
  const isLive = ["connected", "listening", "speaking", "paused"].includes(status);

  // ---------- Screen: setup ----------
  if (!isLive && status !== "ended") {
    return (
      <div data-testid="live-interview-setup" className="max-w-3xl">
        <PageHeader
          eyebrow="Live Interview · Beta"
          title="Realistic AI voice interviews."
          subtitle="Two-way voice · barge-in · server-side VAD · real-time scoring."
        />

        {/* Usage banner — label + reset copy driven by the server-reported period */}
        {usageBanner && (
          <div className={`mb-6 rounded-lg border px-4 py-3 text-sm ${usageBanner.pct > 0.8 ? "bg-[hsl(var(--warning))/0.1] border-[hsl(var(--warning))/0.4] text-[hsl(var(--warning))]" : "bg-muted/40 border-border"}`} data-testid="usage-banner">
            <div className="flex items-center justify-between mb-1">
              <div className="font-mono uppercase text-[10px] tracking-widest" data-testid="usage-banner-label">
                {tier === "premium"
                  ? "Fair-use ceiling"
                  : `${tier.toUpperCase()} plan · ${usageBanner.period === "daily" ? "daily" : "monthly"} usage`}
              </div>
              <div className="text-xs">
                {usageBanner.used_minutes.toFixed(1)} / {usageBanner.limit_minutes} min
              </div>
            </div>
            <div className="h-1.5 bg-background rounded-full overflow-hidden">
              <div className="h-full bg-primary transition-all"
                   style={{ width: `${Math.min(100, usageBanner.pct * 100)}%` }} />
            </div>
            {usageBanner.remaining <= 0 && (
              <div className="mt-2 text-xs" data-testid="usage-banner-limit-reached">
                {usageBanner.period === "daily" ? "Daily limit reached — resets tomorrow." : "Monthly limit reached."}
                {tier !== "premium" && (
                  <> <Link to="/app/pricing" className="underline">Upgrade to Premium</Link> for effectively unlimited sessions.</>
                )}
              </div>
            )}
          </div>
        )}

        {/* Selection form */}
        <div className="bg-card border border-border rounded-xl p-6 space-y-5">
          <SelectorRow
            testid="persona-select"
            label="Interviewer"
            value={selection.persona}
            options={config.personas}
            userTier={tier}
            onChange={(v) => setSelection((s) => ({ ...s, persona: v }))}
          />
          <SelectorRow
            testid="type-select"
            label="Interview type"
            value={selection.interview_type}
            options={config.types}
            userTier={tier}
            onChange={(v) => setSelection((s) => ({ ...s, interview_type: v }))}
          />
          <SelectorRow
            testid="difficulty-select"
            label="Difficulty"
            value={selection.difficulty}
            options={config.difficulties}
            userTier={tier}
            onChange={(v) => setSelection((s) => ({ ...s, difficulty: v }))}
          />
          <div>
            <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Role (optional)</label>
            <input
              data-testid="role-input"
              type="text"
              value={selection.role}
              onChange={(e) => setSelection((s) => ({ ...s, role: e.target.value }))}
              placeholder="e.g. Senior Product Manager"
              className="mt-1 w-full bg-background border border-border rounded-md px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Company (optional)</label>
            <input
              data-testid="company-input"
              type="text"
              value={selection.company}
              onChange={(e) => setSelection((s) => ({ ...s, company: e.target.value }))}
              placeholder={isPaid ? "e.g. Stripe" : "Premium: company-specific interviews"}
              disabled={!isPaid}
              className="mt-1 w-full bg-background border border-border rounded-md px-3 py-2 text-sm disabled:opacity-50"
            />
          </div>

          <Button
            data-testid="start-interview-btn"
            className="w-full h-11 mt-2"
            onClick={start}
            disabled={usageBanner?.remaining <= 0 || status === "connecting"}
          >
            {status === "connecting" ? "Connecting…" : "Start voice interview"}
          </Button>

          <div className="text-[11px] text-muted-foreground text-center">
            Uses your microphone · encrypted WebRTC · you can end the call at any time
          </div>
        </div>
      </div>
    );
  }

  // ---------- Screen: end / scorecard ----------
  if (status === "ended") {
    return (
      <div data-testid="live-interview-scorecard" className="max-w-4xl">
        <PageHeader
          eyebrow={`Interview · ${fmtTime(elapsed)} · ${selection.interview_type}`}
          title="Session complete."
          subtitle={scorecard ? "Your scorecard is ready." : "Generating your scorecard — usually takes 10-30 seconds…"}
        />
        {!scorecard ? (
          <div className="bg-card border border-border rounded-xl p-8 text-center">
            <div className="inline-block h-12 w-12 rounded-full border-2 border-primary border-t-transparent animate-spin" />
            <div className="mt-4 text-sm text-muted-foreground">Analyzing your responses with AI…</div>
          </div>
        ) : (
          <Scorecard sc={scorecard} transcript={transcript} onNew={() => { setStatus("idle"); setElapsed(0); setSessionId(null); }} />
        )}
      </div>
    );
  }

  // ---------- Screen: LIVE ----------
  return (
    <div data-testid="live-interview-live" className={`${fullscreen ? "fixed inset-0 z-50 bg-background p-6" : ""} max-w-6xl`}>
      {/* Status bar */}
      <div className="flex items-center gap-3 mb-4">
        <div className={`h-2 w-2 rounded-full ${status === "listening" ? "bg-red-500 animate-pulse" : status === "speaking" ? "bg-primary animate-pulse" : status === "paused" ? "bg-yellow-500" : "bg-emerald-500"}`} data-testid="status-dot" />
        <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground" data-testid="status-text">{STATUS[status]}</div>
        <div className="text-xs font-mono ml-auto" data-testid="session-timer">{fmtTime(elapsed)}</div>
      </div>

      <div className="grid lg:grid-cols-[420px_1fr] gap-6">
        {/* Left: mic + waveform + controls */}
        <div className="bg-card border border-border rounded-2xl p-6 flex flex-col items-center">
          <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2">
            {isSpeaking ? "Interviewer speaking" : isMuted ? "Muted" : isPaused ? "Paused" : "Your turn"}
          </div>

          {/* Big mic + waveform ring */}
          <div className="relative w-48 h-48 mb-6 flex items-center justify-center" data-testid="mic-orb">
            <div
              className={`absolute inset-0 rounded-full ${isSpeaking ? "bg-primary/20" : "bg-emerald-500/10"} transition-all`}
              style={{ transform: `scale(${1 + waveLevel * 0.5})` }}
            />
            <div
              className={`absolute inset-4 rounded-full border-2 ${isSpeaking ? "border-primary" : "border-emerald-500"} transition-all`}
              style={{ transform: `scale(${1 + waveLevel * 0.3})` }}
            />
            <div className={`z-10 w-32 h-32 rounded-full flex items-center justify-center ${isSpeaking ? "bg-primary text-primary-foreground" : "bg-emerald-500 text-white"}`}>
              {isSpeaking ? <Bot size={44} /> : isMuted ? <MicOff size={44} /> : <Mic size={44} />}
            </div>
          </div>

          {/* Waveform bars */}
          <Waveform level={waveLevel} muted={isMuted} />

          {/* Controls */}
          <div className="mt-6 flex items-center gap-2 flex-wrap justify-center">
            <Button size="icon" variant={isMuted ? "default" : "outline"} onClick={toggleMute} data-testid="mute-btn" title="Mute microphone">
              {isMuted ? <MicOff size={16} /> : <Mic size={16} />}
            </Button>
            <Button size="icon" variant={isPaused ? "default" : "outline"} onClick={togglePause} data-testid="pause-btn" title="Pause">
              {isPaused ? <Play size={16} /> : <Pause size={16} />}
            </Button>
            <Button size="icon" variant="outline" onClick={toggleFullscreen} data-testid="fullscreen-btn" title="Fullscreen">
              {fullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
            </Button>
            <Button variant="destructive" onClick={endInterview} data-testid="end-btn" className="gap-2">
              <PhoneOff size={14} /> End
            </Button>
          </div>

          {/* Current question callout */}
          {currentQuestion && (
            <div className="mt-6 w-full rounded-lg bg-primary/5 border border-primary/20 p-3 text-sm" data-testid="current-question">
              <div className="text-[10px] font-mono uppercase tracking-widest text-primary mb-1">
                <Radio size={10} className="inline mr-1" /> Current question
              </div>
              <div className="text-foreground">{currentQuestion}</div>
            </div>
          )}
        </div>

        {/* Right: live transcript */}
        <div className="bg-card border border-border rounded-2xl p-4 flex flex-col min-h-[520px] max-h-[calc(100vh-200px)]">
          <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-2">
            <Sparkles size={12} /> Live transcript
          </div>
          <div className="flex-1 overflow-y-auto space-y-3 pr-2" data-testid="transcript-scroll">
            {transcript.length === 0 && (
              <div className="text-sm text-muted-foreground italic">
                Say hello to your interviewer to begin. Everything will appear here.
              </div>
            )}
            {transcript.map((t, i) => (
              <TranscriptRow key={`${t.item_id || 'row'}-${t.speaker}-${i}`} row={t} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ------------ Sub-components ------------
function SelectorRow({ label, value, options, onChange, userTier, testid }) {
  const rank = { free: 0, pro: 1, premium: 2 };
  return (
    <div>
      <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">{label}</label>
      <div className="mt-1 grid grid-cols-2 md:grid-cols-3 gap-2" data-testid={testid}>
        {options.map((o) => {
          const minTier = o.min_tier || "free";
          const locked = (rank[userTier] ?? 0) < (rank[minTier] ?? 0);
          return (
            <button
              key={o.key}
              disabled={locked}
              onClick={() => onChange(o.key)}
              data-testid={`${testid}-${o.key}`}
              className={`text-left rounded-lg border p-3 transition-colors relative ${
                value === o.key ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"
              } ${locked ? "opacity-40 cursor-not-allowed" : ""}`}
            >
              <div className="text-sm font-medium">{o.name}</div>
              {o.description && <div className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2">{o.description}</div>}
              {locked && (
                <span className="absolute top-2 right-2 text-[9px] font-mono uppercase tracking-widest bg-muted px-1.5 py-0.5 rounded flex items-center gap-1">
                  <Crown size={9} /> {minTier}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Waveform({ level, muted }) {
  const bars = 12;
  return (
    <div className="flex items-end gap-1 h-8">
      {Array.from({ length: bars }).map((_, i) => {
        const distance = Math.abs(i - bars / 2) / bars;
        const height = muted ? 4 : Math.max(4, level * 32 * (1 - distance * 1.2) + Math.random() * (level > 0.05 ? 8 : 2));
        return (
          <div key={`wave-bar-${i}`}
               className={`w-1 rounded-full transition-all duration-75 ${muted ? "bg-muted" : "bg-emerald-500"}`}
               style={{ height: `${height}px` }} />
        );
      })}
    </div>
  );
}

function TranscriptRow({ row }) {
  const isUser = row.speaker === "user";
  return (
    <div className={`flex gap-2 ${isUser ? "flex-row-reverse" : ""}`}>
      <div className={`h-7 w-7 shrink-0 rounded-full flex items-center justify-center ${isUser ? "bg-emerald-500/15 text-emerald-500" : "bg-primary/15 text-primary"}`}>
        {isUser ? <UserIcon size={14} /> : <Bot size={14} />}
      </div>
      <div className={`text-sm rounded-lg px-3 py-2 max-w-[80%] ${isUser ? "bg-emerald-500/10" : "bg-muted/40"} ${row.partial ? "opacity-70" : ""}`}>
        {row.text || <span className="italic text-muted-foreground">…</span>}
      </div>
    </div>
  );
}

function Scorecard({ sc, transcript, onNew }) {
  const dims = sc.dimensions || {};
  return (
    <div className="space-y-6" data-testid="scorecard">
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center gap-4 mb-4">
          <div className="w-24 h-24 rounded-full flex items-center justify-center bg-primary/10 border-4 border-primary/30">
            <div className="text-3xl font-black" data-testid="scorecard-overall">{sc.overall_score ?? "—"}</div>
          </div>
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Verdict</div>
            <div className="text-2xl font-display font-bold" data-testid="scorecard-verdict">{sc.verdict || "—"}</div>
            <div className="text-sm text-muted-foreground mt-1" data-testid="scorecard-summary">{sc.summary}</div>
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-3 mt-4">
          {Object.entries(dims).map(([k, v]) => (
            <div key={k} className="p-3 bg-muted/30 rounded-lg" data-testid={`dim-${k}`}>
              <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{k.replace(/_/g, " ")}</div>
              <div className="text-2xl font-bold">{v?.score ?? "—"}</div>
              <div className="text-xs text-muted-foreground line-clamp-2">{v?.note}</div>
            </div>
          ))}
        </div>
      </div>

      {(sc.wins?.length > 0 || sc.improvements?.length > 0) && (
        <div className="grid md:grid-cols-2 gap-4">
          {sc.wins?.length > 0 && (
            <div className="bg-card border border-border rounded-xl p-5" data-testid="scorecard-wins">
              <div className="text-xs font-mono uppercase tracking-widest text-emerald-500 mb-2">What worked</div>
              <ul className="space-y-1.5 text-sm">
                {sc.wins.map((w, i) => <li key={`win-${i}-${(w || '').slice(0, 20)}`}>✓ {w}</li>)}
              </ul>
            </div>
          )}
          {sc.improvements?.length > 0 && (
            <div className="bg-card border border-border rounded-xl p-5" data-testid="scorecard-improvements">
              <div className="text-xs font-mono uppercase tracking-widest text-[hsl(var(--warning))] mb-2">Where to improve</div>
              <ul className="space-y-3 text-sm">
                {sc.improvements.map((im, i) => (
                  <li key={`imp-${i}-${(im.issue || '').slice(0, 20)}`}>
                    <div className="font-medium">{im.issue}</div>
                    <div className="text-muted-foreground text-xs">{im.recommendation}</div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <div className="flex gap-3">
        <Button onClick={onNew} data-testid="new-interview-btn">Start another interview</Button>
        <Button variant="outline" asChild><Link to="/app/interview/history">View history</Link></Button>
      </div>
    </div>
  );
}
